/**
 * Produktgalerie auf nativem CSS Scroll-Snap (snippets/product-gallery.liquid).
 *
 * Swipen und Einrasten macht der Browser selbst (Compositor, kein JS pro Frame).
 * Dieses Skript ergaenzt nur:
 * - Thumbnails (Klick, Hover, Drag-Scroll), Dots, Tastatur, Maus-Drag am Desktop
 * - Material-/Rahmen-Filter: Liquid rendert nur die zur Startauswahl passenden Slides,
 *   alle anderen liegen als <template> an ihrer Position. Erst ein Wechsel holt sie
 *   (und damit ihre Bilder) in den DOM.
 * - Rahmen-Overlay, Varianten-Sync, Videos
 */
(function () {
  'use strict';

  const MATERIAL_SUFFIXES = {
    leinwand: ['-leinwand.', '-detail-leinwand.'],
    leinwandbild: ['-leinwand.', '-detail-leinwand.'],
    keilrahmen: ['-leinwand.', '-detail-leinwand.'],
    'alu-geburstet': ['-brushed.', '-detail-brushed.'],
    acrylglas: ['-acryl.', '-detail-acryl.'],
    'acryl-glas': ['-acryl.', '-detail-acryl.'],
    'gallery-print': ['-acryl.', '-detail-acryl.'],
    'alu-weiss': ['-alu.', '-detail-alu.'],
    'alu-weiß': ['-alu.', '-detail-alu.'],
  };
  const ALL_MATERIAL_SUFFIXES = [
    '-leinwand.', '-detail-leinwand.', '-acryl.', '-detail-acryl.',
    '-brushed.', '-detail-brushed.', '-alu.', '-detail-alu.',
  ];

  // Rahmenbilder zeigen das Motiv in schwarzem (-lwrs) oder weissem (-lwrw) Rahmen;
  // nur das zur gewaehlten Rahmenfarbe passende bleibt sichtbar.
  const ALL_FRAME_SUFFIXES = ['-lwrs.', '-lwrw.'];
  const FRAME_KEYS = {
    ohne: 'ohne', without: 'ohne', '': 'ohne',
    schwarz: 'schwarz', black: 'schwarz',
    weiss: 'weiss', 'weiß': 'weiss', white: 'weiss',
    silber: 'silber', silver: 'silber',
    gold: 'gold',
  };

  const DRAG_THRESHOLD = 50;
  const FLICK_VELOCITY = 0.4; // px/ms

  // Bild laden und dekodieren (auch lazy markierte), ohne Fehler zu werfen
  function decodeImage(img) {
    if (!img) return Promise.resolve();
    if (img.loading === 'lazy') img.loading = 'eager';
    return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
  }

  function decodeUrl(url) {
    const img = new Image();
    img.src = url;
    return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
  }

  function wait(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function cleanSlug(value) {
    return String(value || '').trim().toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-')
      .replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  }

  function matchesSuffixes(src, all, selected) {
    if (!all.some((s) => src.includes(s))) return true;
    return !!selected && selected.some((s) => src.includes(s));
  }

  class ProductGallery {
    constructor(root) {
      root._productGallery = this;
      this.root = root;
      this.section = root.closest('.shopify-section') || document;
      this.viewport = root.querySelector('[data-gallery-viewport]');
      this.track = root.querySelector('[data-gallery-track]');
      this.dotsNode = root.querySelector('[data-gallery-dots]');
      this.thumbs = root.querySelector('[data-product-thumbs]');
      this.thumbScroller = root.querySelector('[data-thumb-scroller]');
      this.frameOverlay = root.querySelector('[data-frame-overlay]');
      this.hapticOverlay = root.querySelector('[data-haptic-overlay]');
      this.isVerticalThumbs = this.thumbs && this.thumbs.dataset.position === 'beside';

      // Slides/Thumbs in Originalreihenfolge; ausgefilterte stecken noch im <template>
      this.slides = Array.from(this.track.children).map((node) => ({
        src: node.dataset.imageSrc || '',
        tpl: node.tagName === 'TEMPLATE' ? node : null,
        el: node.tagName === 'TEMPLATE' ? null : node,
      }));
      this.thumbEntries = new Map();
      if (this.thumbScroller) {
        Array.from(this.thumbScroller.children).forEach((node) => {
          this.thumbEntries.set(node.dataset.index, {
            tpl: node.tagName === 'TEMPLATE' ? node : null,
            el: node.tagName === 'TEMPLATE' ? null : node,
          });
        });
      }

      this.material = root.dataset.initialMaterial || '';
      this.frame = root.dataset.initialFrame || '';
      this.visible = this.slides.filter((s) => s.el).map((s) => s.el);
      this.index = 0;

      // Die Standardauswahl (product-template.liquid) setzt Optionen per Skript auf
      // DOMContentLoaded. Die Galerie startet trotzdem beim ersten Bild, daher erst
      // danach Varianten-Wechsel aufs Variantenbild synchronisieren.
      this.syncEnabled = document.readyState !== 'loading';
      if (!this.syncEnabled) {
        document.addEventListener('DOMContentLoaded', () => setTimeout(() => { this.syncEnabled = true; }, 0));
      }

      this.bindViewport();
      this.bindThumbs();
      this.bindOptions();
      this.buildDots();
      // Beim Start steht die Thumbnail-Leiste am Anfang - kein Messen beim Laden noetig
      this.updateActive(true);

      // Weicht die tatsaechliche Startauswahl doch von der serverseitigen ab
      // (z.B. Browser-Zurueck mit gemerkten Radio-Werten), einmal nachziehen.
      const checkedMaterial = this.checkedKey('option1', 'materialKey');
      const checkedFrame = this.checkedFrameKey();
      if ((checkedMaterial !== null && checkedMaterial !== this.material) ||
          (checkedFrame !== null && checkedFrame !== this.frame)) {
        if (checkedMaterial !== null) this.material = checkedMaterial;
        if (checkedFrame !== null) this.setFrame(checkedFrame);
        this.applyFilter();
      }
    }

    /* ---------- Auswahl lesen ---------- */

    frameFieldset() {
      const index = this.frameOverlay && this.frameOverlay.dataset.isGalerieabzug === 'true' ? 'option2' : 'option3';
      return this.section.querySelector(`fieldset[data-index="${index}"]`);
    }

    checkedKey(optionIndex, datasetKey) {
      const input = this.section.querySelector(`fieldset[data-index="${optionIndex}"] input:checked`);
      if (!input) return null;
      return input.dataset[datasetKey] || cleanSlug(input.value);
    }

    checkedFrameKey() {
      const fieldset = this.frameFieldset();
      const input = fieldset && fieldset.querySelector('input:checked');
      if (!input) return null;
      const raw = (input.dataset.frameKey || input.value).trim().toLowerCase();
      return FRAME_KEYS[raw] !== undefined ? FRAME_KEYS[raw] : raw;
    }

    /* ---------- Filter ---------- */

    // Neue Bilder erst laden und dekodieren, dann tauschen; das bisherige Bild blendet
    // als Kopie darueber aus. So gibt es beim Material-/Rahmenwechsel keinen leeren
    // Moment (vorher flackerten Hauptbild und Thumbnails kurz auf).
    async applyFilter(beforeSwap) {
      const token = (this.filterToken = (this.filterToken || 0) + 1);
      const els = this.slidesFor(this.material, this.frame).map((s) => this.materialize(s));
      if (!els.length) {
        if (beforeSwap) beforeSwap();
        return;
      }

      const thumbsVisible = this.thumbScroller && this.thumbs.offsetParent !== null;
      const thumbEls = !this.thumbScroller ? [] : els.map((el) => {
        const entry = this.thumbEntries.get(el.dataset.index);
        if (!entry) return null;
        if (!entry.el) entry.el = document.importNode(entry.tpl.content.firstElementChild, true);
        return entry.el;
      }).filter(Boolean);

      const waits = [decodeImage(els[0].querySelector('img'))];
      if (thumbsVisible) {
        thumbEls.filter((t) => !t.isConnected).forEach((t) => waits.push(decodeImage(t.querySelector('img'))));
      }
      if (this.pendingFrameUrl) waits.push(decodeUrl(this.pendingFrameUrl));
      await Promise.race([Promise.all(waits), wait(1500)]);
      if (token !== this.filterToken) return;

      const firstUnchanged = this.visible[this.index] === els[0];
      const ghost = firstUnchanged && !beforeSwap ? null : this.createGhost();
      const thumbGhost = thumbsVisible ? this.createThumbGhost() : null;
      // Standbilder zeigen bereits geladene Bilder - trotzdem dekodiert abwarten,
      // damit sie im ersten Frame vollstaendig sichtbar sind
      const ghostImgs = [ghost, thumbGhost].filter(Boolean).flatMap((g) => Array.from(g.querySelectorAll('img')));
      await Promise.race([Promise.all(ghostImgs.map(decodeImage)), wait(300)]);
      if (token !== this.filterToken) {
        [ghost, thumbGhost].forEach((g) => g && g.remove());
        return;
      }

      if (beforeSwap) beforeSwap();
      this.track.replaceChildren(...els);
      this.visible = els;

      if (this.thumbScroller) this.thumbScroller.replaceChildren(...thumbEls);

      // Overlays wandern mit auf den ersten sichtbaren Slide
      if (this.hapticOverlay) els[0].appendChild(this.hapticOverlay);
      if (this.frameOverlay) els[0].appendChild(this.frameOverlay);

      // Erst alle DOM-Aenderungen, dann Scrollen und Messen in einem Layout-Durchgang,
      // erst danach die Klassenwechsel (sonst je ein erzwungenes Layout dazwischen)
      if (this.viewport._nsAnim) this.viewport._nsAnim.cancel();
      if (this.thumbScroller) this.thumbScroller.scrollTo({ left: 0, top: 0, behavior: 'instant' });
      this.viewport.scrollTo({ left: 0, behavior: 'instant' });
      this.index = 0;
      this.updateThumbFade();
      this.buildDots();
      // Thumbnail-Leiste steht schon am Anfang, das erste Thumb ist sichtbar
      this.updateActive(true);
      this.pauseVideos();

      [ghost, thumbGhost].filter(Boolean).forEach((g) => {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          g.classList.add('is-fading');
          setTimeout(() => g.remove(), 400);
        }));
      });
    }

    slidesFor(material, frame) {
      const materialSuffixes = MATERIAL_SUFFIXES[material] || null;
      const frameSuffixes = frame === 'schwarz' ? ['-lwrs.'] : frame === 'weiss' ? ['-lwrw.'] : null;
      return this.slides.filter((s) =>
        matchesSuffixes(s.src, ALL_MATERIAL_SUFFIXES, materialSuffixes) &&
        matchesSuffixes(s.src, ALL_FRAME_SUFFIXES, frameSuffixes));
    }

    // Aus dem <template> in das Dokument uebernehmen, damit die Bilder laden koennen
    materialize(slide) {
      if (!slide.el) slide.el = document.importNode(slide.tpl.content.firstElementChild, true);
      return slide.el;
    }

    // Beim Zeigen auf ein Material dessen erstes Bild schon laden - beim Klick ist
    // es dann meist fertig und der Wechsel sofort.
    prefetchMaterial(key) {
      if (!key || key === this.material) return;
      const slides = this.slidesFor(key, this.frame);
      if (slides[0]) decodeImage(this.materialize(slides[0]).querySelector('img'));
      if (this.thumbScroller && this.thumbs.offsetParent !== null) {
        slides.forEach((s) => {
          const entry = this.thumbEntries.get(s.src && (s.el || s.tpl).dataset.index);
          if (entry && !entry.el) {
            entry.el = document.importNode(entry.tpl.content.firstElementChild, true);
            decodeImage(entry.el.querySelector('img'));
          }
        });
      }
    }

    // Standbild der Thumbnail-Leiste mit deckendem Hintergrund: die neuen (bereits
    // geladenen) Thumbnails liegen darunter, die alte Leiste blendet aus.
    createThumbGhost() {
      const scroller = this.thumbScroller;
      const ghost = scroller.cloneNode(true);
      ghost.removeAttribute('data-thumb-scroller');
      ghost.classList.add('product__thumbs-ghost');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.querySelectorAll('[data-thumb-button]').forEach((n) => n.removeAttribute('data-thumb-button'));
      let bg = 'transparent';
      for (let el = this.thumbs; el && el !== document.documentElement; el = el.parentElement) {
        const c = getComputedStyle(el).backgroundColor;
        if (c && c !== 'transparent' && !/rgba\(.*,\s*0\)$/.test(c)) { bg = c; break; }
      }
      Object.assign(ghost.style, {
        position: 'absolute',
        top: scroller.offsetTop + 'px',
        left: scroller.offsetLeft + 'px',
        width: scroller.offsetWidth + 'px',
        height: scroller.offsetHeight + 'px',
        background: bg === 'transparent' ? '#fff' : bg,
      });
      this.thumbs.appendChild(ghost);
      ghost.scrollLeft = scroller.scrollLeft;
      ghost.scrollTop = scroller.scrollTop;
      return ghost;
    }

    // Standbild des aktuell sichtbaren Slides ueber dem Viewport (fuer den Crossfade)
    createGhost() {
      const current = this.visible[this.index];
      if (!current) return null;
      const ghost = document.createElement('div');
      ghost.className = 'product-gallery__ghost';
      ghost.setAttribute('aria-hidden', 'true');
      const copy = current.cloneNode(true);
      copy.classList.remove('product-main-slide', 'is-selected');
      copy.removeAttribute('data-index');
      copy.querySelectorAll('[data-haptic-overlay], script').forEach((n) => n.remove());
      copy.querySelectorAll('[data-frame-overlay]').forEach((n) => n.removeAttribute('data-frame-overlay'));
      ghost.appendChild(copy);
      this.viewport.parentElement.appendChild(ghost);
      return ghost;
    }

    /* ---------- Rahmen-Overlay ---------- */

    frameUrl(key) {
      const overlay = this.frameOverlay;
      if (!overlay) return null;
      return {
        schwarz: overlay.dataset.baseUrlSchwarz,
        weiss: overlay.dataset.baseUrlWeiss,
        silber: overlay.dataset.baseUrlSilber,
        gold: overlay.dataset.baseUrlGold,
      }[key] || null;
    }

    setFrame(key) {
      this.frame = key;
      const overlay = this.frameOverlay;
      if (!overlay) return;
      const url = this.frameUrl(key);
      if (url) {
        if (overlay.getAttribute('src') !== url) overlay.src = url;
        overlay.style.display = '';
        overlay.classList.add('is-visible');
      } else {
        overlay.style.display = 'none';
        overlay.classList.remove('is-visible');
        overlay.removeAttribute('src');
      }
    }

    // Rahmenbilder erst vorladen, wenn der Kunde Richtung Rahmenauswahl geht -
    // nicht mehr pauschal bei jedem Seitenaufruf.
    preloadFrames() {
      if (this.framesPreloaded || !this.frameOverlay) return;
      this.framesPreloaded = true;
      const d = this.frameOverlay.dataset;
      [d.baseUrlSchwarz, d.baseUrlWeiss, d.baseUrlSilber, d.baseUrlGold].forEach((url) => {
        if (url) new Image().src = url;
      });
    }

    /* ---------- Optionen / Varianten ---------- */

    bindOptions() {
      const frameFieldset = this.frameFieldset();
      if (frameFieldset) {
        const preload = () => this.preloadFrames();
        frameFieldset.addEventListener('pointerover', preload, { once: true, passive: true });
        frameFieldset.addEventListener('focusin', preload, { once: true });
        frameFieldset.addEventListener('touchstart', preload, { once: true, passive: true });
      } else if (this.frameOverlay) {
        this.frameOverlay.remove();
        this.frameOverlay = null;
      }

      const materialFieldset = this.section.querySelector('fieldset[data-index="option1"]');
      if (materialFieldset) {
        const prefetch = (e) => {
          const label = e.target.closest('label');
          const input = label && label.htmlFor ? document.getElementById(label.htmlFor) : e.target.closest('input');
          if (input) this.prefetchMaterial(input.dataset.materialKey || cleanSlug(input.value));
        };
        materialFieldset.addEventListener('pointerover', prefetch, { passive: true });
        materialFieldset.addEventListener('touchstart', prefetch, { passive: true });
        materialFieldset.addEventListener('focusin', prefetch);
      }

      this.section.addEventListener('change', (e) => {
        const input = e.target;
        if (!input || input.tagName !== 'INPUT' || !input.checked) return;
        const fieldset = input.closest('fieldset[data-index]');
        if (!fieldset) return;

        if (fieldset.dataset.index === 'option1') {
          const key = input.dataset.materialKey || cleanSlug(input.value);
          if (key !== this.material) {
            this.material = key;
            this.applyFilter();
          }
        } else if (fieldset === frameFieldset) {
          const key = this.checkedFrameKey();
          if (key !== this.frame) {
            this.frame = key;
            this.pendingFrameUrl = this.frameUrl(key);
            this.applyFilter(() => {
              this.pendingFrameUrl = null;
              this.setFrame(key);
            });
          }
        }

        if (this.syncEnabled && fieldset.classList.contains('variant-input-wrap')) {
          setTimeout(() => this.syncVariantImage(), 100);
        }
      });
    }

    variants() {
      if (this._variants === undefined) {
        const el = this.section.querySelector('[data-variant-json]');
        try {
          this._variants = el ? JSON.parse(el.textContent) : [];
        } catch (e) {
          this._variants = [];
        }
      }
      return this._variants;
    }

    syncVariantImage() {
      const variants = this.variants();
      if (!variants.length) return;
      const value = (i) => {
        const input = this.section.querySelector(`fieldset[data-index="option${i}"] input:checked`);
        return input ? input.value : undefined;
      };
      const o1 = value(1), o2 = value(2), o3 = value(3);
      const variant = variants.find((v) =>
        (v.option1 === o1 || (!v.option1 && !o1)) &&
        (v.option2 === o2 || (!v.option2 && !o2)) &&
        (v.option3 === o3 || (!v.option3 && !o3)));
      if (!variant || !variant.featured_image) return;

      const url = variant.featured_image.src.split('?')[0];
      const filename = url.substring(url.lastIndexOf('/') + 1);
      const target = this.visible.findIndex((el) => (el.dataset.imageSrc || '').includes(filename));
      if (target !== -1) this.goTo(target, true);
    }

    /* ---------- Viewport ---------- */

    bindViewport() {
      let ticking = false;
      this.viewport.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          const width = this.viewport.clientWidth;
          if (!width) return;
          const index = Math.round(this.viewport.scrollLeft / width);
          if (index !== this.index) {
            this.index = Math.max(0, Math.min(index, this.visible.length - 1));
            this.updateActive();
            this.handleVideos();
          }
        });
      }, { passive: true });

      this.viewport.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); this.step(-1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); this.step(1); }
      });

      // Maus-Drag am Desktop (Touch scrollt nativ). Ein schneller kurzer Flick
      // blaettert auch unterhalb der Weg-Schwelle weiter.
      let startX = 0, startScroll = 0, dragging = false, moved = false, samples = [];
      this.viewport.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || this.visible.length < 2) return;
        if (this.viewport._nsAnim) this.viewport._nsAnim.cancel();
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = this.viewport.scrollLeft;
        samples = [{ x: e.clientX, t: e.timeStamp }];
        this.viewport.classList.add('is-dragging');
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 3) moved = true;
        this.viewport.scrollTo({ left: startScroll - dx, behavior: 'instant' });
        samples.push({ x: e.clientX, t: e.timeStamp });
        if (samples.length > 6) samples.shift();
      }, { passive: true });
      window.addEventListener('pointerup', (e) => {
        if (!dragging) return;
        dragging = false;
        const dx = e.clientX - startX;
        const recent = samples.filter((p) => e.timeStamp - p.t < 100);
        const first = recent[0] || samples[0];
        const velocity = e.timeStamp > first.t ? (e.clientX - first.x) / (e.timeStamp - first.t) : 0;
        const from = Math.round(startScroll / this.viewport.clientWidth);
        // Wie zuvor mit loop: am Ende geht es von vorne weiter
        if (dx < -DRAG_THRESHOLD || velocity < -FLICK_VELOCITY) this.goTo(from + 1, true, true);
        else if (dx > DRAG_THRESHOLD || velocity > FLICK_VELOCITY) this.goTo(from - 1, true, true);
        else this.goTo(from, true);
        this.viewport.classList.remove('is-dragging');
      });
      this.viewport.addEventListener('click', (e) => {
        if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
      }, true);
    }

    step(delta) {
      this.goTo(this.index + delta, true, true);
    }

    goTo(index, smooth, wrap) {
      const count = this.visible.length;
      if (!count) return;
      if (wrap) index = (index + count) % count;
      index = Math.max(0, Math.min(index, count - 1));
      const left = index * this.viewport.clientWidth;
      if (smooth && window.NativeSlider) {
        NativeSlider.animateTo(this.viewport, left);
      } else {
        if (this.viewport._nsAnim) this.viewport._nsAnim.cancel();
        this.viewport.scrollTo({ left, behavior: smooth ? 'smooth' : 'instant' });
      }
      if (index !== this.index) {
        this.index = index;
        this.updateActive();
        this.handleVideos();
      }
    }

    /* ---------- Dots / aktiver Zustand ---------- */

    buildDots() {
      if (!this.dotsNode) return;
      const frag = document.createDocumentFragment();
      if (this.visible.length > 1) {
        this.visible.forEach((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-gallery__dot';
          dot.setAttribute('aria-label', `Zu Bild ${i + 1}`);
          dot.addEventListener('click', () => this.goTo(i, true));
          frag.appendChild(dot);
        });
      }
      this.dotsNode.replaceChildren(frag);
    }

    updateActive(thumbsAtStart) {
      const current = this.visible[this.index];
      const originalIndex = current ? current.dataset.index : null;

      this.visible.forEach((el, i) => el.classList.toggle('is-selected', i === this.index));

      if (this.dotsNode) {
        Array.from(this.dotsNode.children).forEach((dot, i) => {
          dot.classList.toggle('product-gallery__dot--active', i === this.index);
        });
      }

      if (this.thumbScroller) {
        this.thumbScroller.querySelectorAll('[data-thumb-button]').forEach((btn) => {
          const active = btn.dataset.index === originalIndex;
          btn.classList.toggle('is-active', active);
          if (active) {
            btn.setAttribute('aria-current', 'true');
            if (!thumbsAtStart) this.scrollThumbIntoView(btn.closest('.product__thumb-item'));
          } else {
            btn.removeAttribute('aria-current');
          }
        });
      }
    }

    /* ---------- Videos ---------- */

    handleVideos() {
      this.visible.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        if (i === this.index) {
          video.play().catch(() => {});
        } else {
          video.pause();
          video.currentTime = 0;
        }
      });
    }

    pauseVideos() {
      this.track.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
    }

    /* ---------- Thumbnails ---------- */

    visibleIndexOf(originalIndex) {
      return this.visible.findIndex((el) => el.dataset.index === originalIndex);
    }

    bindThumbs() {
      if (!this.thumbs || !this.thumbScroller) return;
      const scroller = this.thumbScroller;
      let hasDragged = false;

      this.thumbs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-thumb-button]');
        if (!btn) return;
        e.preventDefault();
        if (hasDragged) return;
        const i = this.visibleIndexOf(btn.dataset.index);
        if (i >= 0) this.goTo(i, true);
      });

      // Hover springt direkt auf das Bild (wie bisher)
      scroller.addEventListener('pointerover', (e) => {
        if (e.pointerType !== 'mouse' || isDragging) return;
        const btn = e.target.closest('[data-thumb-button]');
        if (!btn) return;
        const i = this.visibleIndexOf(btn.dataset.index);
        if (i >= 0 && i !== this.index) this.goTo(i, false);
      });

      // Drag-Scroll der Thumbnail-Leiste mit der Maus
      let isDragging = false, start = 0, startScroll = 0;
      scroller.addEventListener('mousedown', (e) => {
        e.preventDefault();
        isDragging = true;
        hasDragged = false;
        scroller.classList.add('is-dragging');
        start = this.isVerticalThumbs ? e.pageY : e.pageX;
        startScroll = this.isVerticalThumbs ? scroller.scrollTop : scroller.scrollLeft;
      });
      window.addEventListener('mousemove', (e) => {
        if (!isDragging) return;
        const walk = (this.isVerticalThumbs ? e.pageY : e.pageX) - start;
        if (Math.abs(walk) > 5) hasDragged = true;
        if (this.isVerticalThumbs) scroller.scrollTop = startScroll - walk;
        else scroller.scrollLeft = startScroll - walk;
      }, { passive: true });
      window.addEventListener('mouseup', () => {
        if (!isDragging) return;
        isDragging = false;
        scroller.classList.remove('is-dragging');
        setTimeout(() => { hasDragged = false; }, 100);
      });

      scroller.addEventListener('scroll', () => this.updateThumbFade(), { passive: true });
      this.updateThumbFade();
    }

    updateThumbFade() {
      if (!this.thumbScroller || !this.thumbs) return;
      const s = this.thumbScroller;
      const pos = this.isVerticalThumbs ? s.scrollTop : s.scrollLeft;
      const max = this.isVerticalThumbs ? s.scrollHeight - s.clientHeight : s.scrollWidth - s.clientWidth;
      this.thumbs.classList.toggle('scrolled-to-end', pos >= max - 5);
    }

    scrollThumbIntoView(item) {
      if (!item) return;
      const s = this.thumbScroller;
      if (this.isVerticalThumbs) {
        const top = item.offsetTop, h = item.offsetHeight;
        if (top < s.scrollTop) s.scrollTo({ top: top - 20, behavior: 'smooth' });
        else if (top + h > s.scrollTop + s.clientHeight) s.scrollTo({ top: top + h - s.clientHeight + 20, behavior: 'smooth' });
      } else {
        const left = item.offsetLeft, w = item.offsetWidth;
        if (left < s.scrollLeft) s.scrollTo({ left: left - 20, behavior: 'smooth' });
        else if (left + w > s.scrollLeft + s.clientWidth) s.scrollTo({ left: left + w - s.clientWidth + 20, behavior: 'smooth' });
      }
    }
  }

  function init(root) {
    (root || document).querySelectorAll('[data-product-gallery]').forEach((el) => {
      if (!el._productGallery) new ProductGallery(el);
    });
  }

  init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
